import PageTitle from "../Common/PageTitle"
import ToolBar from "../Common/Toolbar"
import { FC, useEffect, useState } from "react"
import { Box, Chip, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button } from "@mui/material"
import { Apis, numberWithCommas } from "../../helpers"
import { PropertyProps } from "../../types/properties"
import { VacationNoticeProps } from "../../types/vacationNotices"
import NoData from "../Common/NoData"
import { useNavigate } from "react-router-dom"
import { Connect, mapStateToProps } from "../../Redux"
import moment from 'moment'

interface StateProps {
    data: VacationNoticeProps[]
    loading: boolean
    refreshing: boolean
}
interface Props {
    selectedProperty?: PropertyProps
}

const STATUS_COLOR: Record<string, 'default' | 'warning' | 'info' | 'success' | 'error'> = {
    PENDING: 'default',
    PROCESSING: 'info',
    MAINTENANCE_REVIEW: 'warning',
    ACCEPTED: 'success',
    REJECTED: 'error',
    DISPUTED: 'error',
    COMPLETED: 'success',
    CANCELLED: 'default'
}

const STATUS_LABEL: Record<string, string> = {
    PENDING: 'Pending',
    PROCESSING: 'Processing',
    MAINTENANCE_REVIEW: 'Awaiting Tenant',
    ACCEPTED: 'Accepted',
    REJECTED: 'Rejected',
    DISPUTED: 'Disputed',
    COMPLETED: 'Completed',
    CANCELLED: 'Cancelled'
}

const VacationNotices: FC<Props> = ({ selectedProperty }) => {
    const [state, setState] = useState<StateProps>({
        data: [],
        loading: true,
        refreshing: false
    })
    const { data, loading, refreshing } = state
    const navigate = useNavigate()

    const getNotices = ({ refreshing }: any = {}) => {
        if (refreshing) setState(s => ({ ...s, refreshing: true }))
        Apis({
            module: 'VacationNotices',
            action: 'getPropertyNotices',
            propertId: selectedProperty?._id
        }).then((resp: any) => {
            setState(s => ({ ...s, data: Array.isArray(resp) ? resp : [], loading: false, refreshing: false }))
        }).catch(() => {
            setState(s => ({ ...s, loading: false, refreshing: false }))
        })
    }

    useEffect(() => {
        getNotices()
        // eslint-disable-next-line
    }, [selectedProperty?._id])

    const canProcess = selectedProperty?.role === 'Owner' || selectedProperty?.role === 'Agent'

    return (
        <Box sx={{ height: '100%', pr: 5 }}>
            <PageTitle title={"Vacation Notices"} />
            {!loading && data.length ?
                <ToolBar
                    noSearch
                    noAddButton
                    refreshing={refreshing}
                    onRefresh={() => getNotices({ refreshing: true })}
                />
                : null}
            {
                !data?.length || loading ?
                    <NoData
                        message={`No vacation notices have been submitted for this property.`}
                        loading={loading}
                    /> :
                    <Paper sx={{ width: '100%' }}>
                        <TableContainer sx={{ maxHeight: 520 }}>
                            <Table stickyHeader aria-label="sticky table">
                                <TableHead>
                                    <TableRow>
                                        {['Tenant', 'House', 'Notice Date', 'Move-Out Date', 'Status', 'Action'].map(label => (
                                            <TableCell sx={{ fontWeight: 'bold' }} key={label}>{label}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.map((notice) => (
                                        <TableRow hover key={notice._id}>
                                            <TableCell>{notice.tenant ? `${notice.tenant.firstName || ''} ${notice.tenant.lastName || ''}`.trim() : 'N/A'}</TableCell>
                                            <TableCell>{notice.unitLabel} - {notice.unitName}</TableCell>
                                            <TableCell>{moment(notice.createdAt).format('DD/MM/YYYY')}</TableCell>
                                            <TableCell>{moment(notice.moveOutDate).format('DD/MM/YYYY')}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={STATUS_LABEL[notice.status] || notice.status}
                                                    color={STATUS_COLOR[notice.status] || 'default'}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    disabled={!canProcess}
                                                    onClick={() => navigate('/vacation_notices/process', { state: { vacationNoticeId: notice._id } })}
                                                >
                                                    {['PENDING', 'PROCESSING', 'MAINTENANCE_REVIEW', 'DISPUTED'].includes(notice.status) ? 'Process' : 'View'}
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
            }
        </Box>
    )
}

export default Connect(mapStateToProps)(VacationNotices)
